import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { canSee, jeSubdodavatel, subObchod } from "./lib/nav";
import { Spinner } from "./components/ui/kit";
import AppLayout from "./components/AppLayout";

import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import VerifyEmail from "./pages/VerifyEmail";
import ConfirmEmailChange from "./pages/ConfirmEmailChange";

import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Team from "./pages/Team";
import TeamMemberDetail from "./pages/TeamMemberDetail";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Partneri from "./pages/Partneri";
import PartnerDetail from "./pages/PartnerDetail";
import Calendar from "./pages/Calendar";
import Obchod from "./pages/Obchod";
import PripadDetail from "./pages/PripadDetail";
import TechDetail from "./pages/TechDetail";
import Polozky from "./pages/Polozky";
import ZarizeniCenik from "./pages/ZarizeniCenik";
import { SledovaniCesty } from "./lib/navigace";
import Nabidky from "./pages/Nabidky";
import NabidkaEditor from "./pages/NabidkaEditor";
import Doklady from "./pages/Doklady";
import DokladDetail from "./pages/DokladDetail";
import Ekonomika from "./pages/Ekonomika";
import Sklad from "./pages/Sklad";
import Kpi from "./pages/Kpi";
import Servis from "./pages/Servis";
import ZarizeniDetail from "./pages/ZarizeniDetail";
import ServisZapis from "./pages/ServisZapis";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface text-muted">
      <Spinner />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  return <AppLayout>{children}</AppLayout>;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === undefined) return <FullLoader />;
  if (!canSee(user, roles)) return <Navigate to="/" replace />;
  // Subdodavatel smí na obchod jen s rozsahem OBCHOD (nastavuje majitel).
  if (jeSubdodavatel(user) && roles?.includes("SUBDODAVATEL") && OBCHOD_CESTY.test(pathname) && !subObchod(user))
    return <Navigate to="/" replace />;
  return children;
}
const OBCHOD_CESTY = /^\/(obchod|technologie|nabidky)(\/|$)/;

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

/** /kalendar → /ukoly i s parametry (pohled, jen po termínu). */
function PresmerujNaUkoly() {
  const { search } = useLocation();
  return <Navigate to={`/ukoly${search}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SledovaniCesty />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />

          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/nastaveni" element={<Protected><Settings /></Protected>} />
          <Route path="/tym" element={<Protected><Team /></Protected>} />
          <Route path="/tym/:id" element={<Protected><TeamMemberDetail /></Protected>} />
          <Route path="/klienti" element={<Protected><Clients /></Protected>} />
          <Route path="/klienti/:id" element={<Protected><ClientDetail /></Protected>} />
          <Route path="/partneri" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "ASISTENTKA"]}>
              <Partneri />
            </RoleGuard></Protected>
          } />
          <Route path="/partneri/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "ASISTENTKA"]}>
              <PartnerDetail />
            </RoleGuard></Protected>
          } />
          <Route path="/ukoly" element={<Protected><Calendar /></Protected>} />
          {/* starší odkazy a záložky */}
          <Route path="/kalendar" element={<PresmerujNaUkoly />} />
          <Route path="/obchod" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <Obchod />
            </RoleGuard></Protected>
          } />
          <Route path="/obchod/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <PripadDetail />
            </RoleGuard></Protected>
          } />
          <Route path="/technologie/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <TechDetail />
            </RoleGuard></Protected>
          } />
          <Route path="/ekonomika" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <Ekonomika />
            </RoleGuard></Protected>
          } />
          <Route path="/polozky" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <Polozky />
            </RoleGuard></Protected>
          } />
          <Route path="/cenik/zarizeni/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <ZarizeniCenik />
            </RoleGuard></Protected>
          } />
          <Route path="/doklady" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <Doklady />
            </RoleGuard></Protected>
          } />
          <Route path="/doklady/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <DokladDetail />
            </RoleGuard></Protected>
          } />
          <Route path="/kpi" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK"]}>
              <Kpi />
            </RoleGuard></Protected>
          } />
          <Route path="/sklad" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "ASISTENTKA"]}>
              <Sklad />
            </RoleGuard></Protected>
          } />
          <Route path="/nabidky" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "SUBDODAVATEL"]}>
              <Nabidky />
            </RoleGuard></Protected>
          } />
          <Route path="/nabidky/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "SUBDODAVATEL"]}>
              <NabidkaEditor />
            </RoleGuard></Protected>
          } />
          <Route path="/servis" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <Servis />
            </RoleGuard></Protected>
          } />
          <Route path="/servis/zarizeni/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <ZarizeniDetail />
            </RoleGuard></Protected>
          } />
          <Route path="/servis/zapisy/:id" element={
            <Protected><RoleGuard roles={["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"]}>
              <ServisZapis />
            </RoleGuard></Protected>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
